import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Button, Box, Stack, Alert, Typography } from '@mui/material';
import { RootState, AppDispatch } from '../../store/store';
import { setChecklist, setPartnerProfile, setView, updateFormData } from './onboardingSlice';
import apiClient, { setOnboardingToken } from '../../api/client';
import { fetchMyApplication } from './checklistApi';
import { fetchMyPartner } from './partnerApi';
import { isPartnerAudience } from '../../api/brand';

const ContactInfo = () => {
  const dispatch: AppDispatch = useDispatch();
  const { formData, brandConfig } = useSelector((state: RootState) => state.onboarding);
  const isPartner = isPartnerAudience() || brandConfig?.audience === 'partner';

  const [email, setEmail] = useState(formData.email);
  const [dialCode, setDialCode] = useState('+44');
  const [localPhone, setLocalPhone] = useState(formData.phone);

  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [channel, setChannel] = useState<'email' | 'phone'>('phone');
  const [resendIn, setResendIn] = useState(0);
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = (v: string) => v.trim().length > 0 && emailRe.test(v.trim());
  const countryDialMap: Record<string,string> = useMemo(() => ({
    IN: '+91', GB: '+44', US: '+1', AE: '+971', SA: '+966', FR: '+33', DE: '+49', ES: '+34', IT: '+39', NL: '+31', SE: '+46', PT: '+351', PL: '+48', IE: '+353'
  }), []);

  useEffect(() => {
    if (!resendIn) return;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => {
    // Prefill dial code from IP; fallback to +91 if unknown
    (async () => {
      try {
        const res = await fetch('https://ipapi.co/json');
        const j = await res.json();
        const cc = (j && j.country) ? String(j.country).toUpperCase() : '';
        const code = String((j && j.country_calling_code) || (countryDialMap[cc] || '+91')).trim();
        setDialCode(code.startsWith('+') ? code : `+${code.replace(/^\+/, '')}`);
      } catch (_) {
        setDialCode('+91');
      }
    })();
  }, [countryDialMap]);

  const sendOtp = async () => {
    setOtpError('');
    setEmailError('');
    setLoading(true);
    try {
      if (channel === 'email') {
        const trimmed = email.trim();
        if (!isValidEmail(trimmed)) {
          setEmailError('Please enter a valid email address');
          setLoading(false);
          return;
        }
      }
      const fullPhone = `${dialCode}${localPhone || ''}`;
      const value = channel === 'phone' ? fullPhone : email.trim();
      await apiClient.post('/onboarding/send-otp', { channel, value });
      setOtpSent(true);
      setResendIn(120);
    } catch (e: any) {
      setOtpError(e?.response?.data?.message || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const isLocalHost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const enterHub = async (token?: string, nextEmail?: string, nextPhone?: string) => {
    if (token) setOnboardingToken(token);
    dispatch(updateFormData({
      email: (nextEmail || email).trim(),
      phone: nextPhone || `${dialCode}${localPhone || ''}`,
      phoneCountryCode: dialCode,
      ownerPhoneLocal: (localPhone || '').replace(/\D/g, ''),
    }));
    try {
      if (isPartner) {
        dispatch(setPartnerProfile(await fetchMyPartner()));
      } else {
        const existing = await fetchMyApplication();
        dispatch(setChecklist(existing));
      }
    } catch (_) {
      if (isPartner) dispatch(setPartnerProfile(null));
      else dispatch(setChecklist(null));
    }
    dispatch(setView('hub'));
  };

  const bypassOtp = async () => {
    setOtpError('');
    setEmailError('');
    setLoading(true);
    try {
      const trimmedEmail = email.trim() || 'dev@localhost.test';
      if (email.trim() && !isValidEmail(email.trim())) {
        setEmailError('Please enter a valid email address');
        setLoading(false);
        return;
      }
      const fullPhone = localPhone ? `${dialCode}${localPhone}` : '+447000000000';
      const value = channel === 'phone' ? fullPhone : trimmedEmail;
      const res = await apiClient.post('/onboarding/bypass-otp', {
        channel,
        value,
        email: trimmedEmail,
        phone: fullPhone,
      });
      await enterHub(res.data?.token, trimmedEmail, fullPhone);
    } catch (e: any) {
      setOtpError(e?.response?.data?.message || 'OTP bypass is not available on this API');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpError('');
    setEmailError('');
    setLoading(true);
    try {
      if (channel === 'email' && !isValidEmail(email.trim())) {
        setEmailError('Please enter a valid email address');
        setLoading(false);
        return;
      }
      const fullPhone = `${dialCode}${localPhone || ''}`;
      const value = channel === 'phone' ? fullPhone : email.trim();
      const res = await apiClient.post('/onboarding/verify-otp', { channel, value, code: otp });
      await enterHub(res.data?.token, email.trim(), fullPhone);
    } catch (e: any) {
      setOtpError(e?.response?.data?.message || 'Invalid code');
    } finally { setLoading(false); }
  };

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Email Address"
          variant="outlined"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
          onBlur={() => { const t = email.trim(); if (t && !isValidEmail(t)) setEmailError('Please enter a valid email'); else setEmailError(''); }}
          error={!!emailError}
          helperText={emailError}
          required
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 2fr', sm: '1fr 2fr' }, gap: 1 }}>
          <TextField label="Dial code" value={dialCode} onChange={(e)=>setDialCode(e.target.value)} inputProps={{ inputMode: 'tel', list: 'dial-codes' }} />
          <datalist id="dial-codes">
            {Object.values(countryDialMap).filter((v, i, a) => a.indexOf(v)===i).map((v)=> <option key={v} value={v} />)}
          </datalist>
          <TextField label="Phone Number" value={localPhone} onChange={(e)=>{ const v=e.target.value.replace(/[^0-9]/g,''); setLocalPhone(v); }} inputProps={{ inputMode: 'tel' }} required />
        </Box>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
        <Button variant={channel==='phone'?'contained':'outlined'} onClick={()=>setChannel('phone')}>Verify Phone</Button>
        <Button variant={channel==='email'?'contained':'outlined'} onClick={()=>setChannel('email')}>Verify Email</Button>
        <Button variant="outlined" onClick={sendOtp} disabled={loading || resendIn>0 || (channel==='phone' && !localPhone) || (channel==='email' && (!email.trim() || !isValidEmail(email.trim())))}>
          {otpSent ? (resendIn>0 ? `Resend in ${String(Math.floor(resendIn/60)).padStart(2,'0')}:${String(resendIn%60).padStart(2,'0')}` : 'Resend OTP') : 'Send OTP'}
        </Button>
      </Stack>
      {channel==='phone' && !localPhone && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Enter a phone number to send an OTP.</Typography>
      )}
      {channel==='email' && (!email.trim() || !isValidEmail(email.trim())) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Enter a valid email address to send an OTP.</Typography>
      )}

      {otpSent && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
          <TextField label="Enter OTP" value={otp} onChange={(e)=>setOtp(e.target.value)} />
          <Button variant="contained" onClick={verifyOtp} disabled={loading || otp.length < 4}>Verify OTP & Continue</Button>
        </Stack>
      )}

      {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}

      {isLocalHost && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" color="warning" onClick={bypassOtp} disabled={loading}>
            Bypass OTP (local)
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Local testing only. Uses the email/phone above, or a placeholder if empty.
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Please see this introduction video before starting</Typography>
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
          <iframe
            src={(typeof window !== 'undefined' && (window as any).__BRAND__ && (window as any).__BRAND__.intro_video_url) || 'https://www.youtube.com/embed/KnQa_YVoSHQ'}
            title="Onboarding Guide"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </Box>
      </Box>
    </>
  );
};

export default ContactInfo;
